import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import {
  Lock,
  ShieldAlert,
  Terminal,
  AlertTriangle,
  ArrowUpRight,
} from "lucide-react";

/**
 * /kiosk-lock — public how-to for venue owners who want a truly locked
 * kiosk. Sony/Google TV lets a determined user hit Home and escape the
 * Mirrorloops shell unless the app is registered as a Device Owner (DPM)
 * on the TV — that's an ADB one-time step this page walks through.
 *
 * Same principle applies to LG webOS via Enterprise Mode.
 */
export default function KioskLock() {
  return (
    <PublicShell>
      <div data-testid="kiosklock-page" className="max-w-4xl mx-auto px-6 pt-32 pb-24">
        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-4">
            <Lock size={12} /> Kiosk Lock · How-to
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
            Hard-lock a TV
            <br />
            <span className="text-[#D4B878]">so the remote can't escape.</span>
          </h1>
          <p className="mt-6 text-white/60 text-base max-w-2xl leading-relaxed">
            The Mirrorloops Signage Player boots in kiosk mode by default,
            but the TV remote's Home button can still exit the app on Sony
            Bravia / Google TV and (with a knowledgeable installer) on LG
            webOS. For venues that need a fully locked screen — retail
            windows, transit hubs, healthcare lobbies — the two flows below
            escalate to real, OS-level kiosk enforcement.
          </p>
        </div>

        {/* Safety notice */}
        <div className="mb-10 border border-amber-400/30 bg-amber-500/[0.06] rounded-lg p-4 flex gap-3">
          <AlertTriangle size={16} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[13px] text-white/70 leading-relaxed">
            <span className="text-amber-200 font-semibold">
              Read this before you start.
            </span>{" "}
            Both flows reduce the TV to a single-purpose kiosk. Factory
            resets are the only reliable way to undo Device Owner mode on
            Android TV. Do this on TVs you own — don't apply it to display
            devices you might redeploy for other uses later.
          </div>
        </div>

        {/* Sony flow */}
        <section
          data-testid="kiosklock-sony"
          className="mb-12 border border-white/10 rounded-lg bg-white/[0.02] p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#00E5FF" }}
            >
              <ShieldAlert size={16} className="text-black" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#00E5FF]">
                Sony Bravia · Google TV / Android TV
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Device Owner mode via ADB
              </h2>
            </div>
          </div>
          <p className="text-white/70 text-[14px] leading-relaxed mb-4">
            Android has a first-class "Device Owner" concept — an app
            elevated to admin-of-the-device. Once Mirrorloops Signage is a
            Device Owner, the Home button, Back button, and even the Sony
            remote's dedicated Netflix/YouTube quick-launch keys are all
            intercepted and swallowed. This is a one-time ADB command;
            after it's set, the TV is locked to Mirrorloops forever
            (until factory reset).
          </p>
          <ol className="space-y-4 text-[13.5px] text-white/80 leading-relaxed list-decimal ml-5">
            <li>
              <span className="text-white font-semibold">
                Install the Mirrorloops Signage APK
              </span>{" "}
              on the TV. Follow the standard install guide from{" "}
              <Link to="/downloads" className="text-[#D4B878] underline decoration-dotted underline-offset-2">
                /downloads
              </Link>{" "}
              — get it running and paired first.
            </li>
            <li>
              <span className="text-white font-semibold">
                Enable Developer Options + ADB
              </span>
              : Settings → Device Preferences → About → click Build 7×
              → back → Developer Options → USB debugging + Network
              debugging ON. Note the TV's IP address.
            </li>
            <li>
              <span className="text-white font-semibold">
                Remove any existing account owner
              </span>{" "}
              — Device Owner mode fails if the TV was set up with a Google
              account. From your laptop:
              <pre
                data-testid="kiosklock-cmd-1"
                className="mt-2 p-3 bg-black/60 border border-white/10 rounded text-[12px] font-mono text-emerald-300 overflow-x-auto"
              >
{`adb connect <TV_IP>:5555
adb shell pm list users
# Remove any secondary user; keep only user 0
adb shell pm remove-user <userId>`}
              </pre>
            </li>
            <li>
              <span className="text-white font-semibold">
                Grant Device Owner to Mirrorloops
              </span>
              :
              <pre
                data-testid="kiosklock-cmd-2"
                className="mt-2 p-3 bg-black/60 border border-white/10 rounded text-[12px] font-mono text-emerald-300 overflow-x-auto"
              >
{`adb shell dpm set-device-owner \\
    com.mirrorloops.signage/.DeviceAdminReceiver`}
              </pre>
              Expected response:{" "}
              <code className="text-[11px] text-white/60">
                Success: Device owner set to package …
              </code>
            </li>
            <li>
              <span className="text-white font-semibold">Reboot the TV</span>
              . On next boot, Mirrorloops takes over: the Home button no
              longer exits, and the input source key is blocked. The venue
              can still change volume — the DPM policy leaves audio
              controls alone by design.
            </li>
          </ol>

          <div className="mt-5 p-3 border border-white/10 rounded bg-black/40 text-[12px] text-white/60">
            <span className="text-[#00E5FF] font-semibold">Undo:</span>{" "}
            Device Owner can't be removed without factory reset. Plan
            accordingly — this is a one-way door.
          </div>
        </section>

        {/* LG flow */}
        <section
          data-testid="kiosklock-lg"
          className="mb-12 border border-white/10 rounded-lg bg-white/[0.02] p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#E8D6A3" }}
            >
              <ShieldAlert size={16} className="text-black" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E8D6A3]">
                LG webOS
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Enterprise Mode via SI Program
              </h2>
            </div>
          </div>
          <p className="text-white/70 text-[14px] leading-relaxed mb-4">
            Consumer LG UHDs (55NU870BPLA and friends) don't ship a Device
            Owner equivalent — for hard kiosk on LG you want the SI (System
            Integrator) Program, which enables "Auto Launch" and blocks
            the LG Home overlay. Two paths:
          </p>
          <ol className="space-y-4 text-[13.5px] text-white/80 leading-relaxed list-decimal ml-5">
            <li>
              <span className="text-white font-semibold">
                Consumer LG UHD (recommended for &lt; 20 screens):
              </span>{" "}
              open the built-in Web Browser → set the Mirrorloops pair URL
              as the startup page (Settings → General → Startup) → hide
              the LG Home shortcut on the remote by using masking tape.
              Not a hard lock, but 95% of venue staff will never reach for
              a lower key. This is the flow the standard{" "}
              <Link to="/downloads" className="text-[#D4B878] underline decoration-dotted underline-offset-2">
                LG install guide
              </Link>{" "}
              already covers.
            </li>
            <li>
              <span className="text-white font-semibold">
                LG Signage panels (LG SM/UM Series, hard lock):
              </span>{" "}
              step up to LG's dedicated commercial displays (SM5J, UM3E)
              and enrol them in LG SI Server via the LG Partner Portal.
              Once enrolled, the TV boots directly into the assigned URL
              with all consumer input hidden. This is the correct answer
              for public retail / transit / healthcare use where hard
              lock is a compliance requirement. LG's commercial team
              handles enrolment; typical lead time 5–10 working days.
              <a
                href="https://partner.lge.com"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 inline-flex items-center gap-0.5 text-[#D4B878] hover:text-[#E8D6A3] underline decoration-dotted underline-offset-2"
              >
                LG Partner Portal
                <ArrowUpRight size={11} />
              </a>
            </li>
          </ol>
        </section>

        {/* When to use hard lock */}
        <section
          data-testid="kiosklock-when"
          className="mb-12 border border-white/10 rounded-lg bg-white/[0.02] p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#D4B878]">
              <Terminal size={16} className="text-black" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#D4B878]">
                Decision guide
              </div>
              <h2 className="text-2xl font-semibold text-white">
                When do you actually need this?
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                need: true,
                title: "Public transit, retail windows, ATMs",
                body: "Anyone can walk up with a spare remote or a paired phone; you want the display fixed.",
              },
              {
                need: true,
                title: "Healthcare / regulated environments",
                body: "Compliance often requires demonstrably locked kiosk mode with an audit trail.",
              },
              {
                need: false,
                title: "Café, restaurant, gym",
                body: "Staff are trusted; the default kiosk (no hard lock) is fine and it's easier to redeploy the TV if the venue closes.",
              },
              {
                need: false,
                title: "Office lobby, hotel reception",
                body: "You want the TV to be usable if a receptionist needs to jump to the news — leave it soft.",
              },
            ].map((c) => (
              <div
                key={c.title}
                className={`p-3 rounded border ${
                  c.need
                    ? "border-[#CCFF00]/25 bg-[#CCFF00]/[0.04]"
                    : "border-white/10 bg-white/[0.02]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-[9px] uppercase tracking-[0.22em] font-semibold ${
                      c.need ? "text-[#CCFF00]" : "text-white/50"
                    }`}
                  >
                    {c.need ? "Hard lock" : "Default kiosk"}
                  </span>
                </div>
                <div className="text-sm text-white font-medium mb-1">
                  {c.title}
                </div>
                <div className="text-[12px] text-white/60 leading-relaxed">
                  {c.body}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[11px] text-white/40">
          <div>
            Need help with an enrolment? Email{" "}
            <a
              href="mailto:hello@mirrorloops.com"
              className="text-[#D4B878] hover:text-[#E8D6A3] underline decoration-dotted underline-offset-2"
            >
              hello@mirrorloops.com
            </a>
            . Publishing the signed APK? See{" "}
            <a
              href="https://github.com/mirrorloops/mirrorloops/tree/main/native/sony-android-tv/play-console"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4B878] hover:text-[#E8D6A3] underline decoration-dotted underline-offset-2"
              data-testid="kiosklock-play-console-link"
            >
              /native/sony-android-tv/play-console
            </a>{" "}
            for the ready-to-copy store listing kit.
          </div>
          <div className="flex gap-4">
            <Link to="/downloads" className="hover:text-white">
              Downloads →
            </Link>
            <Link to="/concierge" className="hover:text-[#D4B878]">
              Concierge →
            </Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
